import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = 'https://api.glowytics.com';

function buildHeaders(brandId) {
  return {
    'Content-Type': 'application/json',
    'x-brand-id': brandId || '',
  };
}

const microsToTL = (m) => (Number(m || 0) / 1_000_000).toFixed(2);
const tlToMicros = (tl) => Math.round(Number(tl || 0) * 1_000_000);

function Diff({ before, after, mono = false }) {
  if (before === after || after == null || after === '') {
    return <span style={{ fontFamily: mono ? 'ui-monospace, monospace' : undefined }}>{before || '—'}</span>;
  }
  return (
    <span style={{ fontFamily: mono ? 'ui-monospace, monospace' : undefined }}>
      <span style={{ background: '#fee2e2', color: '#991b1b', textDecoration: 'line-through', padding: '0 4px', borderRadius: 3 }}>{before}</span>
      <span style={{ margin: '0 6px', color: '#6b7280' }}>→</span>
      <span style={{ background: '#d1fae5', color: '#065f46', padding: '0 4px', borderRadius: 3 }}>{after}</span>
    </span>
  );
}

function Section({ title, children, right }) {
  return (
    <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#111827' }}>{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function AdminGoogleAdsClone() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || '';

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState('');

  const [sourceAccountId, setSourceAccountId] = useState('');
  const [targetAccountId, setTargetAccountId] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState('');
  const [campaignId, setCampaignId] = useState('');

  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState('');

  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [newName, setNewName] = useState('');
  const [budgetTL, setBudgetTL] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState('');
  const [pushResult, setPushResult] = useState(null);

  // Load accounts when active brand changes.
  useEffect(() => {
    if (!brandId) return;
    setAccountsLoading(true);
    setAccounts([]);
    setSourceAccountId('');
    setTargetAccountId('');
    setCampaigns([]);
    setCampaignId('');
    setTree(null);
    setPreview(null);
    setPushResult(null);
    fetch(`${API_BASE}/api/admin/google-ads-clone/accounts`, {
      headers: buildHeaders(brandId),
      credentials: 'include',
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Failed to load accounts');
        setAccounts(j.accounts || []);
        setAccountsError('');
        const def = (j.accounts || []).find((a) => a.isDefault) || (j.accounts || [])[0];
        if (def) {
          setSourceAccountId(def.id);
          setTargetAccountId(def.id);
        }
      })
      .catch((e) => setAccountsError(e.message))
      .finally(() => setAccountsLoading(false));
  }, [brandId]);

  const loadCampaigns = useCallback(() => {
    if (!brandId || !sourceAccountId) return;
    setCampaignsLoading(true);
    setCampaigns([]);
    setCampaignId('');
    setTree(null);
    setPreview(null);
    setPushResult(null);
    fetch(`${API_BASE}/api/admin/google-ads-clone/campaigns?accountId=${encodeURIComponent(sourceAccountId)}`, {
      headers: buildHeaders(brandId),
      credentials: 'include',
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Failed to load campaigns');
        setCampaigns(j.campaigns || []);
        setCampaignsError('');
      })
      .catch((e) => setCampaignsError(e.message))
      .finally(() => setCampaignsLoading(false));
  }, [brandId, sourceAccountId]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadTree = useCallback(() => {
    if (!brandId || !sourceAccountId || !campaignId) return;
    setTreeLoading(true);
    setTree(null);
    setPreview(null);
    setPushResult(null);
    fetch(`${API_BASE}/api/admin/google-ads-clone/campaign-tree?accountId=${encodeURIComponent(sourceAccountId)}&campaignId=${encodeURIComponent(campaignId)}`, {
      headers: buildHeaders(brandId),
      credentials: 'include',
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Failed to load tree');
        setTree(j.tree);
        setTreeError('');
        const dailyBudget = j.tree?.campaign?.daily_budget_micros || 0;
        if (dailyBudget > 0) setBudgetTL(microsToTL(dailyBudget));
      })
      .catch((e) => setTreeError(e.message))
      .finally(() => setTreeLoading(false));
  }, [brandId, sourceAccountId, campaignId]);

  const runPreview = () => {
    if (!brandId || !sourceAccountId || !campaignId || !find) return;
    setPreviewLoading(true);
    setPreview(null);
    setPushResult(null);
    fetch(`${API_BASE}/api/admin/google-ads-clone/preview`, {
      method: 'POST',
      headers: buildHeaders(brandId),
      credentials: 'include',
      body: JSON.stringify({
        sourceAccountId, sourceCampaignId: campaignId,
        find, replace, newCampaignName: newName || undefined,
      }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Preview failed');
        setPreview(j);
        setPreviewError('');
        if (!newName && j.new_campaign_name) setNewName(j.new_campaign_name);
      })
      .catch((e) => setPreviewError(e.message))
      .finally(() => setPreviewLoading(false));
  };

  const runPush = () => {
    if (!brandId || !sourceAccountId || !targetAccountId || !campaignId || !find || !newName || !budgetTL) return;
    const tgtLabel = accounts.find((a) => a.id === targetAccountId)?.label || targetAccountId;
    if (!window.confirm(`Push "${newName}" as PAUSED to Google Ads account ${tgtLabel}?\n\nBudget: ${budgetTL} TL/day\nFind: "${find}" → Replace: "${replace}"`)) return;
    setPushing(true);
    setPushResult(null);
    fetch(`${API_BASE}/api/admin/google-ads-clone/push`, {
      method: 'POST',
      headers: buildHeaders(brandId),
      credentials: 'include',
      body: JSON.stringify({
        sourceAccountId, sourceCampaignId: campaignId, targetAccountId,
        find, replace, newCampaignName: newName,
        dailyBudgetMicros: tlToMicros(budgetTL),
      }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok) throw new Error(j.error || 'Push failed');
        setPushResult(j);
        setPushError('');
      })
      .catch((e) => setPushError(e.message))
      .finally(() => setPushing(false));
  };

  const tgt = useMemo(() => accounts.find((a) => a.id === targetAccountId), [accounts, targetAccountId]);
  const previewTree = preview?.tree || null;

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
  const buttonStyle = (disabled) => ({
    padding: '8px 16px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600,
    background: disabled ? '#e5e7eb' : '#6366f1', color: disabled ? '#9ca3af' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
  });
  const dangerButton = (disabled) => ({
    padding: '10px 20px', borderRadius: 6, border: 'none', fontSize: 14, fontWeight: 700,
    background: disabled ? '#e5e7eb' : '#dc2626', color: disabled ? '#9ca3af' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  if (!selectedBrand) {
    return (
      <div style={{ padding: 40, fontSize: 14, color: '#6b7280' }}>
        Please select a brand from the brand switcher to use this tool.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', background: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px 0', color: '#111827' }}>
          Google Ads — Search Campaign Cloner
        </h1>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>
          Brand: <strong>{selectedBrand.name}</strong> · Push always goes out as <strong>PAUSED</strong> · Find is case-insensitive · Source and target accounts must both belong to this brand.
        </div>

        {accountsError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 }}>{accountsError}</div>}

        {/* ─── Step 1: Source account + campaign ─── */}
        <Section title="1. Pick a source Google Ads account + Search campaign">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Source account</label>
              <select value={sourceAccountId} onChange={(e) => setSourceAccountId(e.target.value)} style={inputStyle} disabled={accountsLoading}>
                <option value="">{accountsLoading ? 'Loading…' : accounts.length === 0 ? 'No Google Ads accounts on this brand' : '— select —'}</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.label} ({a.customerId}){a.isDefault ? ' ★' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Search campaign</label>
              <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} style={inputStyle} disabled={!sourceAccountId || campaignsLoading}>
                <option value="">
                  {campaignsLoading ? 'Loading…' : campaigns.length === 0 ? (sourceAccountId ? 'No Search campaigns' : '— pick account first —') : '— select —'}
                </option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} [{c.status}] — {microsToTL(c.daily_budget_micros)} TL/day</option>
                ))}
              </select>
            </div>
          </div>
          {campaignsError && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 12 }}>{campaignsError}</div>}
          <div style={{ marginTop: 12 }}>
            <button onClick={loadTree} disabled={!sourceAccountId || !campaignId || treeLoading} style={buttonStyle(!sourceAccountId || !campaignId || treeLoading)}>
              {treeLoading ? 'Loading tree…' : 'Load campaign tree'}
            </button>
          </div>
          {treeError && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 12 }}>{treeError}</div>}
        </Section>

        {/* ─── Step 2: Tree ─── */}
        {tree && (
          <Section title="2. Source tree" right={
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {tree.ad_groups.length} ad groups · {tree.ad_groups.reduce((s, ag) => s + ag.keywords.length, 0)} keywords · {tree.ad_groups.reduce((s, ag) => s + ag.ads.length, 0)} RSAs
            </span>
          }>
            <TreeView tree={tree} />
          </Section>
        )}

        {/* ─── Step 3: Find / Replace ─── */}
        {tree && (
          <Section title="3. Find &amp; replace">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Find (case-insensitive)</label>
                <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="fabletics" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Replace with</label>
                <input value={replace} onChange={(e) => setReplace(e.target.value)} placeholder="XXX" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>New campaign name (auto-filled from replace; you can override)</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="(auto)" style={inputStyle} />
            </div>
            <div style={{ marginTop: 12 }}>
              <button onClick={runPreview} disabled={!find || previewLoading} style={buttonStyle(!find || previewLoading)}>
                {previewLoading ? 'Computing diff…' : 'Preview changes'}
              </button>
            </div>
            {previewError && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 12 }}>{previewError}</div>}
          </Section>
        )}

        {/* ─── Step 4: Preview diff ─── */}
        {preview && previewTree && (
          <Section title="4. Replace preview" right={
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {preview.changes} change{preview.changes === 1 ? '' : 's'}
              {preview.warnings?.length > 0 && (
                <span style={{ marginLeft: 12, color: '#b45309' }}>⚠ {preview.warnings.length} warning{preview.warnings.length === 1 ? '' : 's'}</span>
              )}
            </span>
          }>
            {preview.warnings?.length > 0 && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Warnings</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <DiffTreeView tree={previewTree} />
          </Section>
        )}

        {/* ─── Step 5: Push ─── */}
        {preview && (
          <Section title="5. Push to a Google Ads account on this brand (PAUSED)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Target account (same brand)</label>
                <select value={targetAccountId} onChange={(e) => setTargetAccountId(e.target.value)} style={inputStyle}>
                  <option value="">— select —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.label} ({a.customerId}){a.isDefault ? ' ★' : ''}</option>
                  ))}
                </select>
                {tgt && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                    Customer: <code>{tgt.customerId}</code>
                    {tgt.managerCustomerId && <> · MCC: <code>{tgt.managerCustomerId}</code></>}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Daily budget (TL)</label>
                <input type="number" step="0.01" min="1" value={budgetTL} onChange={(e) => setBudgetTL(e.target.value)} placeholder="100" style={inputStyle} />
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  = {tlToMicros(budgetTL).toLocaleString('tr-TR')} micros
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                onClick={runPush}
                disabled={!targetAccountId || !newName || !budgetTL || pushing}
                style={dangerButton(!targetAccountId || !newName || !budgetTL || pushing)}
              >
                {pushing ? 'Pushing to Google Ads…' : '🚀 Push as PAUSED'}
              </button>
            </div>
            {pushError && (
              <div style={{ marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {pushError}
              </div>
            )}
            {pushResult && (
              <div style={{ marginTop: 12, background: '#d1fae5', color: '#065f46', padding: 14, borderRadius: 6 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>✓ Created PAUSED on customer {pushResult.customer_id}</div>
                <div style={{ fontSize: 13 }}>
                  Campaign ID: <code>{pushResult.new_campaign_id}</code><br />
                  {pushResult.ad_group_count} ad groups · {pushResult.keyword_count} keywords · {pushResult.ad_count} RSAs<br />
                  Name: <strong>{pushResult.new_campaign_name}</strong>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: '#065f46' }}>
                  Open in Google Ads: <a target="_blank" rel="noopener noreferrer" href={`https://ads.google.com/aw/campaigns?campaignId=${pushResult.new_campaign_id}`} style={{ color: '#065f46', textDecoration: 'underline' }}>https://ads.google.com/aw/campaigns?campaignId={pushResult.new_campaign_id}</a>
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}

// ─── Read-only tree view ─────────────────────────────────────────────────────
function TreeView({ tree }) {
  const cam = tree.campaign;
  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ marginBottom: 10, padding: 10, background: '#f3f4f6', borderRadius: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{cam.name}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          {cam.status} · {cam.bidding_strategy_type} · {microsToTL(cam.daily_budget_micros)} TL/day · {tree.campaign_locations.length} locations · {tree.campaign_languages.length} languages
          {tree.campaign_negative_keywords.length > 0 && <> · {tree.campaign_negative_keywords.length} campaign-neg KWs</>}
        </div>
      </div>
      {tree.ad_groups.map((ag) => (
        <details key={ag.id} style={{ marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <summary style={{ padding: '8px 12px', cursor: 'pointer', background: '#fafafa', borderRadius: 6 }}>
            <strong>{ag.name}</strong> <span style={{ color: '#6b7280', fontSize: 12 }}>· {ag.status} · {ag.keywords.length} kw · {ag.ads.length} ad{ag.ads.length === 1 ? '' : 's'}</span>
          </summary>
          <div style={{ padding: 12 }}>
            {ag.keywords.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Keywords</div>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <tbody>
                    {ag.keywords.map((kw, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'ui-monospace, monospace', color: kw.negative ? '#991b1b' : '#111827' }}>
                          {kw.negative ? '−' : ''} {kw.text}
                        </td>
                        <td style={{ padding: '4px 8px', color: '#6b7280', width: 80 }}>{kw.match_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {ag.ads.map((ad, i) => (
              <div key={ad.id} style={{ marginTop: 10, padding: 10, background: '#fafafa', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>RSA #{i + 1} · {ad.status}</div>
                {(ad.final_urls || []).map((u, j) => <div key={j} style={{ fontSize: 12, color: '#1d4ed8' }}>{u}</div>)}
                {(ad.path1 || ad.path2) && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Path: /{ad.path1}{ad.path2 ? `/${ad.path2}` : ''}</div>
                )}
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Headlines ({ad.headlines.length})</div>
                  {ad.headlines.map((h, j) => <div key={j} style={{ fontSize: 12 }}>• {h.text}{h.pinned_field && h.pinned_field !== 'UNSPECIFIED' && <span style={{ color: '#6366f1' }}> 📌{h.pinned_field}</span>}</div>)}
                </div>
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Descriptions ({ad.descriptions.length})</div>
                  {ad.descriptions.map((d, j) => <div key={j} style={{ fontSize: 12 }}>• {d.text}{d.pinned_field && d.pinned_field !== 'UNSPECIFIED' && <span style={{ color: '#6366f1' }}> 📌{d.pinned_field}</span>}</div>)}
                </div>
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// ─── Diff tree view ──────────────────────────────────────────────────────────
function DiffTreeView({ tree }) {
  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ marginBottom: 10, padding: 10, background: '#f3f4f6', borderRadius: 6 }}>
        <div style={{ fontWeight: 700 }}>
          <Diff before={tree.campaign.name_before} after={tree.campaign.name_after} />
        </div>
      </div>
      {tree.ad_groups.map((ag) => (
        <details key={ag.id} open style={{ marginBottom: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <summary style={{ padding: '8px 12px', cursor: 'pointer', background: '#fafafa' }}>
            <Diff before={ag.name_before} after={ag.name_after} />
          </summary>
          <div style={{ padding: 12 }}>
            {ag.keywords.length > 0 && (
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 10 }}>
                <tbody>
                  {ag.keywords.map((kw, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 8px' }}>
                        {kw.negative ? <span style={{ color: '#991b1b' }}>− </span> : null}
                        <Diff mono before={kw.text_before} after={kw.text_after} />
                      </td>
                      <td style={{ padding: '4px 8px', color: '#6b7280', width: 80 }}>{kw.match_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {ag.ads.map((ad, i) => (
              <div key={ad.id} style={{ marginTop: 10, padding: 10, background: '#fafafa', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>RSA #{i + 1}</div>
                {ad.final_urls_before?.map((u, j) => (
                  <div key={j} style={{ fontSize: 12 }}>
                    <Diff mono before={u} after={ad.final_urls_after?.[j]} />
                  </div>
                ))}
                {(ad.path1_before || ad.path2_before) && (
                  <div style={{ fontSize: 12 }}>
                    Path: /<Diff mono before={ad.path1_before} after={ad.path1_after} />{ad.path2_before ? <>/<Diff mono before={ad.path2_before} after={ad.path2_after} /></> : null}
                  </div>
                )}
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Headlines</div>
                  {ad.headlines.map((h, j) => (
                    <div key={j} style={{ fontSize: 12 }}>• <Diff before={h.text_before} after={h.text_after} /></div>
                  ))}
                </div>
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Descriptions</div>
                  {ad.descriptions.map((d, j) => (
                    <div key={j} style={{ fontSize: 12 }}>• <Diff before={d.text_before} after={d.text_after} /></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}
